// Centralised content + data for Simba Travel

export const COMPANY = {
  name: "Simba Travel",
  legalName: "Simba Limited",
  registration: "09422922",
  domain: "simbatravel.co.uk",
  tagline: "UK Airline GSA & Representation",
  hours: "9:00 AM – 11:00 PM (UK Time)",
  market: "United Kingdom",
  headOffice: "13 Hogarth Place, London SW5 0QT, United Kingdom",
  addressLines: [
    "Simba Limited — Reg. 09422922",
    "UK-based Air Zimbabwe GSA",
    "13 Hogarth Place, London",
    "SW5 0QT, United Kingdom",
  ],
};

export const DEPARTMENTS = [
  {
    id: "agency-sales",
    name: "Agency Sales",
    email: "Sales@simbatravel.co.uk",
    phone: "02030237701",
    phoneIntl: "+44 20 3023 7701",
    description:
      "Trade rates, fare quotes and BSP support for UK travel agents.",
  },
  {
    id: "ticketing",
    name: "Ticketing",
    email: "Ticketing@simbatravel.co.uk",
    phone: "02030237701",
    phoneIntl: "+44 20 3023 7701",
    description:
      "Ticket issuance, reissues, void requests and complex multi-segment itineraries.",
  },
  {
    id: "refunds",
    name: "Refunds",
    email: "Refunds@simbatravel.co.uk",
    phone: "02030237702",
    phoneIntl: "+44 20 3023 7702",
    description:
      "Refund applications, ADM queries and credit shell management for partner carriers.",
  },
  {
    id: "groups",
    name: "Groups",
    email: "Groups@simbatravel.co.uk",
    phone: "02030237703",
    phoneIntl: "+44 20 3023 7703",
    description:
      "Groups of 10+ passengers, sports teams, conferences, weddings and incentive travel.",
  },
  {
    id: "support",
    name: "Customer Support",
    email: "support@simbatravel.co.uk",
    phone: "02030237704",
    phoneIntl: "+44 20 3023 7704",
    description:
      "General customer support, travel agent registration queries and passenger assistance.",
  },
];

export const SERVICES = [
  {
    slug: "general-sales-agency",
    name: "General Sales Agency (GSA)",
    short:
      "End-to-end GSA representation for airlines entering or scaling within the United Kingdom.",
    long:
      "Simba Travel acts as the official commercial face of partner airlines across the UK & Ireland. We deliver full revenue accountability, BSP settlement, regulatory compliance and trade-channel distribution for the British market.",
    deliverables: [
      "BSP-UK settlement and reporting",
      "CAA & ATOL liaison where applicable",
      "Local bank account & treasury management",
      "Monthly performance dashboards",
      "Quarterly business reviews",
    ],
  },
  {
    slug: "passenger-sales",
    name: "Passenger Sales",
    short:
      "Dedicated B2B and B2C passenger sales teams driving point-of-sale revenue from the UK.",
    long:
      "A multi-lingual sales force focused on filling seats — from leisure travellers visiting friends and relatives, to high-yield corporate and group bookings. We localise fare strategy for the UK market.",
    deliverables: [
      "Inside and outside sales teams",
      "Fare filing co-ordination with ATPCO",
      "Promotional fare campaigns",
      "Yield-led pricing recommendations",
    ],
  },
  {
    slug: "marketing-representation",
    name: "Marketing Representation",
    short:
      "Brand-building campaigns, PR and trade marketing tailored for the UK travel landscape.",
    long:
      "From co-op campaigns with major OTAs to influencer programmes, exhibitions and consumer PR, our marketing team turns route launches into headlines and bookings.",
    deliverables: [
      "Annual marketing plan & calendar",
      "Digital, OOH and trade press buying",
      "ABTA / WTM / Routes exhibition support",
      "Press releases & media liaison",
    ],
  },
  {
    slug: "route-development",
    name: "Route Development",
    short:
      "Data-driven route planning, airport negotiations and slot strategy for new and existing services.",
    long:
      "We support airlines from feasibility to launch — engaging UK airports, slot co-ordinators and tourism boards to secure favourable terms and inaugural-flight visibility.",
    deliverables: [
      "Market sizing & demand forecasts",
      "Slot strategy at LHR, LGW, MAN, BHX",
      "Airport incentive negotiation",
      "Launch campaign management",
    ],
  },
  {
    slug: "distribution-management",
    name: "Distribution Management",
    short:
      "Channel strategy across GDS, NDC, OTAs and metasearch — optimised for UK point of sale.",
    long:
      "We manage inventory visibility and parity across Amadeus, Sabre, Travelport and NDC-enabled partners, ensuring fares reach the right channels at the right cost.",
    deliverables: [
      "GDS bias and incentive programmes",
      "NDC partner onboarding",
      "Metasearch placement (Skyscanner, Google Flights)",
      "Channel cost analysis & reporting",
    ],
  },
  {
    slug: "travel-trade-engagement",
    name: "Travel Trade Engagement",
    short:
      "Active engagement with the UK trade — from independents to consortia and TMCs.",
    long:
      "Familiarisation trips, agent academies, trade fairs and a dedicated trade portal keep our partner airlines top-of-mind across the United Kingdom.",
    deliverables: [
      "Agent training & accreditation programmes",
      "Consortia partnerships (Advantage, ATD, GTMC)",
      "Educational fam trips",
      "Trade newsletters & incentives",
    ],
  },
  {
    slug: "call-centre-services",
    name: "Call Centre Services",
    short:
      "UK-based contact centre operating 9 AM – 11 PM, 7 days a week.",
    long:
      "Our trained airline agents handle reservations, schedule changes, ancillary sales and disruption recovery on behalf of partner carriers — fully branded to your airline.",
    deliverables: [
      "Multi-channel (phone, email, chat)",
      "Branded IVR and call recording",
      "Service level reporting",
      "Surge capacity for IRROPS",
    ],
  },
  {
    slug: "ticketing-operations",
    name: "Ticketing Operations",
    short:
      "24-hour ticketing desk handling issuance, reissues, voids and complex fares.",
    long:
      "From simple point-to-point fares to multi-stop intercontinental itineraries, our ticketing experts hold every major GDS accreditation and follow strict industry TAM standards.",
    deliverables: [
      "Same-day issuance",
      "EMD ancillary handling",
      "ARC/BSP reconciliation",
      "ADM dispute management",
    ],
  },
  {
    slug: "corporate-sales",
    name: "Corporate Sales",
    short:
      "Bespoke corporate account programmes for blue-chip clients and SMEs across the UK.",
    long:
      "Dedicated account managers negotiate corporate deals, manage TMC relationships and grow share of wallet through tailored fare products and reporting.",
    deliverables: [
      "Corporate fare agreements",
      "Quarterly business reviews",
      "Online booking tool support",
      "TMC partnerships",
    ],
  },
];

// Destination data — content tailored for UK travellers connecting via Air Zimbabwe & partners
export const DESTINATIONS = [
  {
    slug: "london",
    name: "London",
    country: "United Kingdom",
    iata: "LHR",
    hero: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=1600&q=80",
    thumb: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=900&q=80",
    overview:
      "Your point of departure. London — the United Kingdom's global capital and the launchpad for Air Zimbabwe passengers heading south to the heart of Africa.",
    flightInfo:
      "Air Zimbabwe operates 3× weekly nonstop services between London Gatwick (LGW) and Harare (HRE) — see the full flight schedule on the Air Zimbabwe partner page.",
    bestTime:
      "May to September for warmest, longest days; November to February for festive markets and quiet museum crowds.",
    attractions: [
      "Tower of London & Tower Bridge",
      "Buckingham Palace & St James's Park",
      "British Museum",
      "The Shard & South Bank",
      "West End theatre district",
    ],
    guide:
      "Fly from London Heathrow — our agency sales desk (+44 20 3023 7701) can co-ordinate onward Air Zimbabwe connections and any UK-side ground arrangements.",
  },
  {
    slug: "harare",
    name: "Harare",
    country: "Zimbabwe",
    iata: "HRE",
    hero: "https://images.pexels.com/photos/31207738/pexels-photo-31207738.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    thumb: "https://images.pexels.com/photos/31207738/pexels-photo-31207738.jpeg?auto=compress&cs=tinysrgb&w=900&q=80",
    overview:
      "Zimbabwe's leafy, jacaranda-lined capital is the country's political and commercial heart. Harare combines modern business hubs with rich colonial architecture, vibrant arts scenes and easy access to safari country.",
    flightInfo:
      "Air Zimbabwe operates 3× weekly nonstop services from London Gatwick (LGW) into Robert Gabriel Mugabe International (HRE), Harare — the airline's primary hub. Full schedule on the Air Zimbabwe partner page.",
    bestTime: "May to October — cool, dry winter season with crisp skies.",
    attractions: [
      "National Gallery of Zimbabwe",
      "Mbare Musika market",
      "Lake Chivero Recreational Park",
      "Wild is Life Wildlife Sanctuary",
      "Domboshawa cave paintings",
    ],
    guide:
      "Stay in the leafy Borrowdale or Avondale suburbs. Try the local sadza and nyama at Amanzi Restaurant, and time your visit around the Harare International Festival of the Arts (HIFA) in May.",
  },
  {
    slug: "mutare",
    name: "Mutare",
    country: "Zimbabwe",
    iata: "UTA",
    hero: "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    thumb: "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg?auto=compress&cs=tinysrgb&w=900&q=80",
    overview:
      "Zimbabwe's easternmost city and gateway to the Eastern Highlands — a lush, mountainous region on the Mozambican border famous for its cool climate, tea estates and dramatic scenery.",
    flightInfo:
      "Air Zimbabwe regional services connect Harare to Mutare Airport (UTA).",
    bestTime: "April to October — dry, clear highland weather.",
    attractions: [
      "Nyanga National Park",
      "Bvumba Mountains botanical gardens",
      "Cross Kopje monument",
      "Mutarazi Falls",
      "Chimanimani National Park",
    ],
    guide:
      "Combine Mutare with a two-night stay in the Bvumba Mountains for cool-weather hiking, birding and Eastern Highlands tea-country tours.",
  },
  {
    slug: "bulawayo",
    name: "Bulawayo",
    country: "Zimbabwe",
    iata: "BUQ",
    hero: "https://images.unsplash.com/photo-1568632234157-ce7aecd03d0d?auto=format&fit=crop&w=1600&q=80",
    thumb: "https://images.unsplash.com/photo-1568632234157-ce7aecd03d0d?auto=format&fit=crop&w=900&q=80",
    overview:
      "Zimbabwe's second city is the gateway to Matobo National Park and a treasure-trove of colonial architecture, museums and steam-railway heritage.",
    flightInfo:
      "Domestic connections from Harare onward to Bulawayo (BUQ) operate daily on Air Zimbabwe's regional network.",
    bestTime: "April to October.",
    attractions: [
      "Matobo National Park & Cecil Rhodes' grave",
      "Khami Ruins (UNESCO)",
      "Natural History Museum",
      "Bulawayo Railway Museum",
      "Hillside Dams",
    ],
    guide:
      "Pair a city stay with two nights at a Matobo lodge for rhino tracking and prehistoric rock art experiences.",
  },
  {
    slug: "victoria-falls",
    name: "Victoria Falls",
    country: "Zimbabwe",
    iata: "VFA",
    hero: "https://images.pexels.com/photos/27878405/pexels-photo-27878405.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    thumb: "https://images.pexels.com/photos/27878405/pexels-photo-27878405.jpeg?auto=compress&cs=tinysrgb&w=900&q=80",
    overview:
      "Mosi-oa-Tunya — 'The Smoke that Thunders' — is one of the Seven Natural Wonders of the World. The Zimbabwean side offers the most panoramic views and an unforgettable adventure-tourism scene.",
    flightInfo:
      "Air Zimbabwe operates services to Victoria Falls Airport (VFA) from Harare.",
    bestTime:
      "February to May for peak flow; September to December for clearest viewing and white-water rafting.",
    attractions: [
      "Devil's Pool at Livingstone Island",
      "Zambezi sunset cruise",
      "Bungee jumping from Victoria Falls Bridge",
      "Helicopter 'Flight of Angels'",
      "Big-five game drives in Zambezi NP",
    ],
    guide:
      "Stay at The Victoria Falls Hotel for old-world colonial charm, or Matetsi River Lodge for upscale safari luxury.",
  },
  {
    slug: "dar-es-salaam",
    name: "Dar es Salaam",
    country: "Tanzania",
    iata: "DAR",
    hero: "https://images.pexels.com/photos/1268855/pexels-photo-1268855.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    thumb: "https://images.pexels.com/photos/1268855/pexels-photo-1268855.jpeg?auto=compress&cs=tinysrgb&w=900&q=80",
    overview:
      "Tanzania's commercial capital and largest city, Dar es Salaam is a vibrant Indian Ocean port that blends Swahili, Arab, Indian and colonial German influences into one of East Africa's most colourful metropolises. Sitting on a natural harbour opposite Zanzibar, the city is the springboard to the Serengeti, Kilimanjaro, Selous Game Reserve and the spice islands — making it a key link between Air Zimbabwe's Southern African network and the wider East African region.",
    flightInfo:
      "Air Zimbabwe operates scheduled services between Harare and Julius Nyerere International Airport (DAR) in Dar es Salaam. From the United Kingdom, passengers connect via Harare on the London Gatwick — Harare service. Simba Travel's UK ticketing desk can build combined LGW–HRE–DAR itineraries with a single PNR and one baggage allowance.",
    bestTime:
      "June to October offers the driest, coolest and most pleasant weather — perfect for city sightseeing and combining Dar with a safari or Zanzibar extension. November and March–May bring the two rainy seasons; December–February is hot but excellent for beach days on nearby islands.",
    attractions: [
      "Coco Beach on the Msasani Peninsula",
      "National Museum & House of Culture",
      "Mwenge Carvers' Market (Makonde art)",
      "Bongoyo Island snorkelling day trip",
      "Kariakoo Market — East Africa's largest",
      "Kivukoni Fish Market at sunrise",
      "St Joseph's Cathedral & German colonial quarter",
    ],
    guide:
      "Use Dar es Salaam as a 24- to 48-hour layover before transferring by 20-minute hop to Zanzibar Stone Town, or as the gateway to southern safari circuits (Nyerere/Selous and Ruaha). Stay on the Msasani Peninsula for beachfront hotels and international dining, or in the CBD near the Askari Monument for business travellers. Traffic through the city can be heavy at peak hours, so allow generous transfer time to and from the airport. English is widely spoken in tourism and business settings, while Swahili is the official language — 'Karibu sana' (welcome) and 'Asante' (thank you) go a long way. The Tanzanian Shilling is the local currency, though USD is accepted at hotels and larger restaurants.",
  },
];

export const AIR_ZIMBABWE = {
  name: "Air Zimbabwe",
  code: "UM",
  founded: 1967,
  hub: "Robert Gabriel Mugabe International Airport (HRE), Harare",
  fleet: [
    { type: "Boeing 767-200ER", count: 1, seats: 250, range: "12,200 km" },
    { type: "Boeing 737-200", count: 2, seats: 115, range: "4,200 km" },
    { type: "Embraer ERJ-145", count: 2, seats: 50, range: "2,800 km" },
  ],
  baggage: {
    economy: "2 pieces × 23 kg (hold) + 1 × 10 kg (cabin)",
    business: "3 pieces × 32 kg (hold) + 1 × 10 kg (cabin)",
    notes:
      "Excess baggage rates available at the airport or via Simba Travel Ticketing.",
  },
  checkIn: {
    online: "24 hours – 90 minutes before departure at airzimbabwe.aero",
    counter:
      "Counters open 3 hours before scheduled departure; close 60 minutes before.",
  },
  routes: [
    "London (Gatwick) — Harare",
    "Harare — Bulawayo",
    "Harare — Mutare",
    "Harare — Victoria Falls",
    "Harare — Dar es Salaam",
  ],
  schedule: {
    title: "London Gatwick ↔ Harare",
    subtitle: "Northern Summer Schedule · Effective July 2026",
    frequency: "3× weekly",
    seasonEnd: "24 October 2026",
    rows: [
      {
        rotation: "Sun → Mon",
        effective: "22 Jul – 27 Sep 2026",
        outbound: "LGW 21:25 → HRE 09:05 (+1)",
        inbound: "HRE 10:35 → LGW 20:05",
        flightNo: "UM723 / UM722",
      },
      {
        rotation: "Mon → Sun",
        effective: "05 Oct – 19 Oct 2026",
        outbound: "LGW 18:30 → HRE 06:00 (+1)",
        inbound: "HRE 22:30 → LGW 08:00 (+1)",
        flightNo: "UM725 / UM724",
      },
      {
        rotation: "Thu → Wed",
        effective: "22 Jul – 27 Sep 2026",
        outbound: "LGW 19:10 → HRE 06:40 (+1)",
        inbound: "HRE 22:30 → LGW 08:00 (+1)",
        flightNo: "UM725 / UM724",
      },
      {
        rotation: "Thu → Wed",
        effective: "01 Oct – 22 Oct 2026",
        outbound: "LGW 19:10 → HRE 06:40 (+1)",
        inbound: "HRE 22:30 → LGW 08:00 (+1)",
        flightNo: "UM725 / UM724",
      },
      {
        rotation: "Sat → Fri",
        effective: "18 Jul – 24 Oct 2026",
        outbound: "LGW 18:20 → HRE 05:50 (+1)",
        inbound: "HRE 22:30 → LGW 08:00 (+1)",
        flightNo: "UM725 / UM724",
      },
    ],
    bullets: [
      "Departures ex-London Gatwick: Sunday (22 Jul – 27 Sep), shifting to Monday from 05 – 19 Oct, plus Thursday and Saturday year-round through 24 Oct.",
      "Departures ex-Harare (return to UK): Sunday, Wednesday and Friday year-round through 24 Oct.",
      "All flights operate nonstop — no technical or fuel stop on either leg.",
    ],
    footnote:
      "(+1) denotes arrival on the following calendar day relative to the departure date shown. All times local.",
  },
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Airline Partners", to: "/airline-partners" },
  { label: "Destinations", to: "/destinations" },
  { label: "Travel Agent Centre", to: "/travel-agent-centre" },
  { label: "Agent Registration", to: "/travel-agent-registration" },
  { label: "Corporate & Groups", to: "/corporate-groups" },
  { label: "Contact", to: "/contact" },
];
